<template>
  <div class="container-custom" :style="styleObject">
    <header>
      <Header></Header>
    </header>
    <div class="ksk-su__scroll" v-if="isLoadDone">
      <!-- KSK016: POS failure — payment went through but the order did not reach POS.
           Same status layout as the payment screens (.ksk-pay, kiosk-theme2.css). -->
      <section
        v-if="
          orderDetail &&
          !updateOrderStatusWithoutPOS &&
          (message_error_from_megapos || orderDetail.is_expired)
        "
        class="ksk-pay ksk-su__fail"
      >
        <div class="ksk-pay__icon">
          <span class="material-icons-round">priority_high</span>
        </div>
        <div class="ksk-pay__title">{{ $t("kiosk.success.orderNotSent") }}</div>
        <div v-if="message_error_from_megapos" class="ksk-pay__caption">
          {{ message_error_from_megapos }}
        </div>
        <div v-else class="ksk-pay__caption">
          {{ $t("kiosk.success.requestTimeout") }}
        </div>
        <!-- Payment reference to show at the counter. -->
        <div class="ksk-su__card ksk-su__ref">
          <div class="ksk-su__ref-row">
            <span>{{ $t("kiosk.success.amountPaid") }}</span>
            <strong>$ {{ paidAmount }}</strong>
          </div>
          <div v-if="orderDetail.status != 0 && orderDetail.payment_method" class="ksk-su__ref-row">
            <span>{{ $t("kiosk.success.paidBy") }}</span>
            <strong>{{ orderDetail.payment_method }}</strong>
          </div>
          <div v-if="orderDetail.order_number" class="ksk-su__ref-row">
            <span>{{ $t("kiosk.success.orderNumber") }}</span>
            <strong>{{ merchantCode.toLocaleUpperCase() }} - {{ orderDetail.order_number }}</strong>
          </div>
          <div class="ksk-su__ref-row">
            <span>{{ $t("kiosk.success.transactionId") }}</span>
            <strong>{{ transactionId }}</strong>
          </div>
        </div>
      </section>

      <template v-else>
        <!-- KSK016: hero -->
        <div class="ksk-su__hero">
          <span class="ksk-su__check" :class="{ 'ksk-su__check--done': confettiActive }">
            <span class="material-icons-round">check_circle</span>
          </span>
          <h1 class="ksk-su__title">{{ $t("kiosk.success.placed") }}</h1>
          <div class="ksk-su__subtitle">{{ $t("kiosk.success.thankYou") }}</div>
        </div>

        <!-- KSK016: order number -->
        <section class="ksk-su__card ksk-su__order">
          <div class="ksk-su__eyebrow">{{ $t("kiosk.success.orderNumber") }}</div>
          <div class="ksk-su__order-no">
            {{ merchantCode.toLocaleUpperCase() }} - {{ orderDetail.order_number }}
          </div>
          <span
            v-if="branch?.show_dining_option == 1 && (orderDetail.dining_option == TAKE_AWAY || orderDetail.dining_option == DINE_IN)"
            class="ksk-su__dining"
          >
            <span class="material-icons-round">
              {{ orderDetail.dining_option == TAKE_AWAY ? 'shopping_bag' : 'restaurant' }}
            </span>
            {{ $t(orderDetail.dining_option == TAKE_AWAY ? "kiosk.common.takeAway" : "kiosk.common.dineIn") }}
          </span>
        </section>

        <!-- KSK016: store / transaction -->
        <section class="ksk-su__card ksk-su__info">
          <div class="ksk-su__info-branch">
            {{ MERCHANT_BRAND_NAME ? MERCHANT_BRAND_NAME + " - " : "" }}{{ branch.branch_name }}
          </div>
          <div class="ksk-su__info-row">
            <span>{{ $t("kiosk.success.idLine", { id: transactionId }) }}</span>
          </div>
        </section>

        <!-- KSK016: order updates — a step tracker (payment → preparing) -->
        <section
          v-if="orderDetail && !orderDetail.action_required"
          class="ksk-su__card"
        >
          <h2 class="ksk-su__card-title">{{ $t("kiosk.success.orderUpdates") }}</h2>
          <div class="ksk-su__steps">
            <div
              class="ksk-su__step"
              :class="{
                'ksk-su__step--done': isPaymentStepDone,
                'ksk-su__step--current': status == 0,
              }"
            >
              <span class="ksk-su__step-dot">
                <span v-if="isPaymentStepDone" class="material-icons-round">check</span>
              </span>
              <span class="ksk-su__step-label">{{ $t("kiosk.success.paymentReceived") }}</span>
            </div>
            <span class="ksk-su__step-line" :class="{ 'ksk-su__step-line--done': status == 1 }"></span>
            <div
              class="ksk-su__step"
              :class="{
                'ksk-su__step--done': status == 1,
                'ksk-su__step--current': status == 1,
                'ksk-su__step--fail': isPreparingStepFailed,
              }"
            >
              <span class="ksk-su__step-dot">
                <span v-if="status == 1" class="material-icons-round">check</span>
                <span v-else-if="isPreparingStepFailed" class="material-icons-round">close</span>
              </span>
              <span class="ksk-su__step-label">{{ $t("kiosk.success.preparing") }}</span>
            </div>
          </div>
          <div v-if="orderDetail" class="ksk-su__paid">
            {{ $t("kiosk.success.paid") + " $" }}
            {{ (
              (Number(orderDetail.amount_direct_deduct) > 0
                ? Number(orderDetail.amount_direct_deduct)
                : (Number(orderDetail.payment_amount) + Number(orderDetail.bcrs_total_amount))
              )
            ).toFixed(2) }}
            <span v-if="orderDetail.status != 0">
              {{ $t("kiosk.success.by") }}
              <strong>{{ orderDetail?.payment_method }}</strong>
            </span>
            <span v-if="orderDetail.payment_date">
              {{ " " + $t("kiosk.success.on") }}
              <span>{{ formatDateTime(orderDetail.payment_date) }}</span>
            </span>
          </div>
        </section>

        <!-- KSK016: order items -->
        <section
          v-if="orderDetail && !orderDetail.action_required"
          class="ksk-su__card"
        >
          <h2 class="ksk-su__card-title">{{ $t("kiosk.success.orderItems") }}</h2>
          <div class="ksk-su__lines">
            <div
              v-for="(item, index) in itemSelect"
              :key="index"
              class="ksk-su__line"
            >
              <img :src="item.image" alt="" class="ksk-su__img" />
              <div class="ksk-su__body">
                <!-- Price on the name row: the item total and the modifiers'
                     "+$" lines below share one right edge. -->
                <div class="ksk-su__name-row">
                  <div class="ksk-su__name">
                    <span class="ksk-su__qty">{{ item.quantity }}×</span>
                    {{ tName(item.item_name) }}<span
                      v-if="isEnableBcrs && parseInt(item.eligible_bcrs) === 1"
                      class="ksk-su__pill"
                      >BCRS</span
                    >
                  </div>
                  <!-- Receipt style: the item's own price here, its add-ons as
                       "+$" lines below, the amount paid in the total row.
                       Lines without a base price keep the line total. -->
                  <div v-if="getMainItemBaseSubTotal(item)" class="ksk-su__price">
                    <div v-if="getMainItemRegularBase(item)" class="ksk-su__price-old">
                      {{ getMainItemRegularBase(item) }}
                    </div>
                    <div class="ksk-su__price-total">{{ getMainItemBaseSubTotal(item) }}</div>
                  </div>
                  <div v-else class="ksk-su__price">
                    <div
                      v-if="item.member_price != item.price_default"
                      class="ksk-su__price-old"
                    >
                      {{ getMainItem(item, isNonMemberForItem(item)) }}
                    </div>
                    <div class="ksk-su__price-total">
                      {{ getMainItemPriceAndModifiersPrice(item, isNonMemberForItem(item)) }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="item.bundle_item && item.bundle_item.length"
                  class="ksk-su__bundle"
                >
                  <div
                    v-for="(bundleItem, bundleIndex) in item.bundle_item"
                    :key="bundleIndex"
                  >
                    {{ bundleItem.quantity }}&nbsp;x&nbsp;{{ tName(bundleItem.itemName) }}
                  </div>
                </div>
                <template v-if="item.choice && item.choice_selected">
                  <template
                    v-for="(itemChoice, indexChoice) in item.choice_selected.items"
                    :key="indexChoice"
                  >
                    <div v-if="itemChoice.item_detail" class="ksk-su__choice">
                      <div class="ksk-su__choice-name">
                        {{ itemChoice.qty + " x " + tName(itemChoice.item_detail.item_name) }}<span
                          v-if="isEnableBcrs && parseInt(itemChoice.item_detail.eligible_bcrs) === 1"
                          class="ksk-su__pill"
                          >BCRS</span
                        >
                      </div>
                      <KskCartModifiers
                        :groups="itemChoice.item_detail.modifier_group"
                        :show-bcrs="isEnableBcrs"
                        :price="getModifierSubTotal"
                        stacked
                      />
                    </div>
                  </template>
                </template>
                <KskCartModifiers
                  v-else
                  :groups="item.modifier_group"
                  :show-bcrs="isEnableBcrs"
                  :price="getModifierSubTotal"
                  stacked
                />
              </div>
            </div>
          </div>
          <div class="ksk-su__total">
            <div class="ksk-su__total-row">
              <span>{{ $t("kiosk.success.totalPaid") }}</span>
              <span class="ksk-su__total-amount">$ {{ paidAmount }}</span>
            </div>
          </div>
        </section>
      </template>
    </div>

    <!-- KSK016: bottom bar — same branches and handlers as before -->
    <div class="ksk-su__bar">
      <div
        v-if="
          orderDetail &&
          !updateOrderStatusWithoutPOS &&
          (message_error_from_megapos || orderDetail.is_expired)
        "
        class="ksk-su__bar-row"
      >
        <KskButton size="md" icon="refresh" class="ksk-su__btn" @click="retry()">
          {{ $t("kiosk.common.retry") }}
        </KskButton>
        <KskCountdownRing :seconds="seconds" />
      </div>
      <div v-else class="ksk-su__bar-row">
        <KskButton
          v-if="
            updateOrderStatusWithoutPOS ||
            (orderDetail && orderDetail.is_pushed_to_megapos_database)
          "
          size="md"
          icon="restart_alt"
          class="ksk-su__btn"
          @click="newOrder()"
        >
          {{ $t("kiosk.success.newOrder") }}
        </KskButton>
        <KskButton v-else size="md" class="ksk-su__btn" disabled>
          <span class="ksk-su__wait">
            <span class="ksk-su__wait-spinner"></span>
            {{ $t("kiosk.success.sendingKitchen") }}
          </span>
        </KskButton>
        <KskCountdownRing
          v-if="
            updateOrderStatusWithoutPOS ||
            (orderDetail && orderDetail.is_pushed_to_megapos_database)
          "
          :seconds="seconds"
        />
      </div>
    </div>
    <div
      class="position-fixed top-1 end-1 message-success"
      style="z-index: 99999 !important"
    >
      <material-snackbar
        v-if="snackbarAsMember === 'danger' || snackbarAsMember === 'success'"
        title=""
        date=""
        :description="messageAsMember"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbarAsMember"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Header from "../kiosk/components/Header.vue";
import KskButton from "./components/ui/KskButton.vue";
import KskCountdownRing from "./components/KskCountdownRing.vue";
import KskCartModifiers from "./components/KskCartModifiers.vue";
import { localStorageName } from "@/config/utils";
import KioskService from "../../services/kiosk.service";
import confetti from "canvas-confetti";
import moment from "moment";
import { TRANSACTION_STATUS_SUCCESS, TAKE_AWAY, DINE_IN } from "@/config/const";
import $ from "jquery";
import OrderRealtime, { signalNeedsRead } from "@/services/realtime";
import { collectNamesDeep, syncMenuTranslations, translatedName } from "@/i18n/menuTranslate";

// POS confirmation arrives over the socket. The timer stays as a safety net —
// websocket delivery is not durable, and a kiosk that lost its socket must still
// confirm the order rather than stand there looking finished.
const FAST_POLL_MS = 3000;
const SLOW_POLL_MS = 30000;

// How long we wait for POS before declaring the order expired.
//
// This used to be counted in poll attempts (60 of them, 3s apart). That number
// silently meant "three minutes" only while the poll rate was fixed, and the
// moment a connected socket slows the timer down, the same 60 attempts would
// have stretched the customer's wait to half an hour. Wall time is what the rule
// was always about, so it is what is measured.
const POS_CONFIRM_WINDOW_MS = 180000;

// Signals arrive in bursts — the payment webhook and the POS callback behind it
// land within a few hundred ms — and each would otherwise cost a full re-read.
const COALESCE_MS = 250;

// Spread the re-reads that follow a websocket-server restart. Far smaller than
// MOO's: a branch has a handful of kiosks rather than a phone per customer, and
// on a screen the customer is standing in front of, a second of delay costs more
// than the thundering herd it prevents.
const RESYNC_JITTER_MS = 1000;

export default {
  name: "PaymentSuccess",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    Header,
    KskButton,
    KskCountdownRing,
    KskCartModifiers,
  },
  data() {
    return {
      isLoadDone: false,
      snackbar: null,
      message: "",
      loaderActive: false,
      merchantCode: "",
      branchCode: "",
      branch: JSON.parse(
        localStorage.getItem(localStorageName("BRANCH_INFO"), "")
      ),
      memberInfo: JSON.parse(
        localStorage.getItem(localStorageName("KIOSK_MEMBER_INFO"), "")
      ),
      MERCHANT_BRAND_NAME: localStorage.getItem(
        localStorageName("MERCHANT_BRAND_NAME"),
        ""
      ),
      isLogined: false,
      intervalCountdown: null,
      intervalOrderStatus: null,
      // Absolute instant we stop waiting for POS. Zero means we are not watching
      // this order any more — the order settled, failed, or ran out of time — and
      // it is what both the timer and an arriving socket signal check before
      // doing anything, so a late signal cannot restart a screen that is already
      // counting down to the kiosk home page.
      pollDeadlineAt: 0,
      deadlineTimer: null,
      pollIntervalMs: null,
      isRefreshing: false,
      // A signal landed while a read was in flight and still owes us one more.
      refreshPending: false,
      refreshTimer: null,
      realtime: null,
      realtimeConnected: false,
      // Set the moment unmounted() starts and checked before anything arms a
      // timer or opens a socket: several of the paths below resume after an
      // await, by which point the screen may be gone.
      isDestroyed: false,
      seconds: 20,
      transactionId: null,
      orderDetail: null,
      itemSelect: null,
      created_at: null,
      status: 0,
      confettiActive: false,
      message_error_from_megapos: "",
      isEnableBcrs: 0,
      updateOrderStatusWithoutPOS: 0,
      readOnFirstConnect: false,
      TRANSACTION_STATUS_SUCCESS,
      TAKE_AWAY,
      DINE_IN,
    };
  },
  async created() {
    this.showLoader();
    this.merchantCode = this.$store.state.merchantCode;
    this.branchCode = this.$store.state.branchCode;
    this.transactionId = this.$route.params.transaction_id ?? null;
    this.isEnableBcrs = parseInt(this.branch?.enable_bcrs) ?? 0;
    this.updateOrderStatusWithoutPOS =
      this.branch?.update_order_status_without_pos || 0;

    if (!this.transactionId) {
      window.location.href = '/' + this.merchantCode + '/' + this.branchCode + '/kiosk';
    }

    // Marked as a refresh so the first-connect read and the first poll tick,
    // both armed by mounted() while this is in flight, queue behind it instead
    // of racing it: an older response landing last would put a settled order
    // back to waiting under the confetti.
    //
    // The finally is what keeps a failed read from leaving the loader up with
    // nothing rendered underneath it.
    this.isRefreshing = true;
    try {
      await this.getOrderDetail();
    } finally {
      this.isRefreshing = false;
      this.hideLoader();
      this.isLoadDone = true;
    }

    // An order already confirmed when the page opened has nothing left to wait
    // for. Waiting for the first poll tick used to cost three seconds; once the
    // socket connects and slows polling down it costs thirty, and the first
    // connection.ready deliberately does not re-read. Anything that asked for a
    // read meanwhile gets it here.
    this.finishOrderRead();

    if (this.memberInfo) {
      this.isLogined = true;
    }
  },
  mounted() {
    $(".modal-backdrop.fade.show").remove();
    this.startPolling();
    // Last, and not awaited: the first paint must not wait on a socket. The
    // first connect reads once more (see onConnectionChange).
    this.startRealtime();
  },
  unmounted() {
    // First line, before any teardown: stopPolling() and realtime.stop() both
    // call back into this component, and this is what makes those harmless.
    this.isDestroyed = true;

    this.stopPolling();
    clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    clearInterval(this.intervalCountdown);
    this.intervalCountdown = null;

    if (this.realtime) {
      this.realtime.stop();
      this.realtime = null;
    }
  },
  watch: {
    // KSK096: language switched from the header — re-translate the order lines.
    "$i18n.locale"() {
      syncMenuTranslations(collectNamesDeep(this.itemSelect));
    },
  },
  computed: {
    // What the shopper was charged (wallet deduction, or payment + BCRS).
    paidAmount() {
      const o = this.orderDetail || {};
      const amount =
        Number(o.amount_direct_deduct) > 0
          ? Number(o.amount_direct_deduct)
          : Number(o.payment_amount) + Number(o.bcrs_total_amount);
      return (Number.isFinite(amount) ? amount : 0).toFixed(2);
    },
    // POS reported the push as failed (is_pushed_to_megapos_database_real 2 / 5).
    isPreparingStepFailed() {
      const real = this.orderDetail?.is_pushed_to_megapos_database_real;
      return this.status != 1 && (real == 2 || real == 5);
    },
    // Same tick as before on "Payment Received" (status 0, POS push not
    // failed); once the order is preparing (status 1) payment is done too.
    isPaymentStepDone() {
      if (this.status == 1) return true;
      const real = this.orderDetail?.is_pushed_to_megapos_database_real;
      return this.status == 0 && !!this.orderDetail && real != 2 && real != 5;
    },
    styleObject() {
      return {
        "--color": this.$store.state.color,
      };
    },
  },
  methods: {
    tName(source) {
      return translatedName(source);
    },

    formatPrice(price) {
      return "$ " + parseFloat(price).toFixed(2);
    },

    formatSubTotalPrice(quantity, unitPrice) {
      const price = parseFloat(unitPrice);
      if (isNaN(price) || price === 0) return "";
      const qty = parseInt(quantity) || 1;
      if (qty > 1) {
        return qty + " x " + this.formatPrice(price);
      }
      return this.formatPrice(price);
    },

    // Whether this line was charged at the regular price. The kiosk session is
    // logged out on the way to this screen, so the order itself is the only
    // reliable source: saveOrder stamps is_member on every line. Orders placed
    // before that stamp existed fall back to the session, as before.
    isNonMemberForItem(item) {
      if (item && Object.prototype.hasOwnProperty.call(item, "is_member")) {
        return !item.is_member;
      }
      return !this.memberInfo;
    },

    getMainItemBaseSubTotal(item) {
      let unitPrice;
      if (
        !this.isNonMemberForItem(item) &&
        item.member_price_no_modifier_option
      ) {
        unitPrice = item.member_price_no_modifier_option;
      } else {
        unitPrice = item.price_default_no_modifier_option;
      }
      return this.formatSubTotalPrice(item.quantity, unitPrice);
    },

    // Member line: the regular base price, struck through next to the member
    // base price (empty when they are the same / not a member line).
    getMainItemRegularBase(item) {
      if (this.isNonMemberForItem(item) || !item.member_price_no_modifier_option) return "";
      const regular = parseFloat(item.price_default_no_modifier_option);
      if (!regular || regular == parseFloat(item.member_price_no_modifier_option)) return "";
      return this.formatSubTotalPrice(item.quantity, regular);
    },

    getModifierSubTotal(modifier) {
      if (!modifier) return "";
      const price = parseFloat(modifier.price_default);
      if (isNaN(price) || price === 0) return "";
      const qty = parseInt(modifier.quantity) || 1;
      if (qty > 1) {
        return "x " + this.formatPrice(price);
      }
      return this.formatPrice(price);
    },

    formatDateTime(date) {
      return moment(date).format("DD/MM/YYYY hh:mm A");
    },

    startCountdown() {
      if (this.intervalCountdown) return;

      this.intervalCountdown = setInterval(() => {
        if (this.seconds > 0) {
          this.seconds--;
        } else {
          clearInterval(this.intervalCountdown);
          this.intervalCountdown = null;
          window.location.href = '/' + this.merchantCode + '/' + this.branchCode + '/kiosk';
        }
      }, 1000);
    },

    async getOrderDetail() {
      var dataForm = {
        branch_code: this.branchCode,
        transaction_id: this.transactionId,
      };

      await KioskService.getOrderDetail(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.orderDetail = response.data.data;
            this.itemSelect = response.data.data.items_ordering_data;
            // KSK054: order lines come from the order record, not the menu batch.
            syncMenuTranslations(collectNamesDeep(this.itemSelect));
            this.updateOrderStatusWithoutPOS = response.data.data.update_order_status_without_pos;

            if (this.updateOrderStatusWithoutPOS) {
              this.orderDetail.is_expired = 0;
              this.message_error_from_megapos = "";
              this.status = 0;
            } else {
              if (
                response.data.data.is_pushed_to_megapos_database &&
                response.data.data.is_pushed_to_megapos_database == 1
              ) {
                this.status = 1;
              } else if (
                response.data.data.is_pushed_to_megapos_database &&
                response.data.data.is_pushed_to_megapos_database == 2
              ) {
                this.status = 2;
              } else {
                this.status = 0;
              }

              this.message_error_from_megapos = response.data.data
                .message_error_from_megapos
                ? response.data.data.message_error_from_megapos
                : "";

              if (
                this.message_error_from_megapos ||
                this.orderDetail.is_expired
              ) {
                // Terminal: no further read moves the order out of these, so the
                // watch ends here. stopPolling() also clears the deadline, which
                // is what stops a socket signal arriving a moment later from
                // reviving a screen that is already counting down.
                this.stopPolling();
                clearInterval(this.intervalCountdown);
                this.intervalCountdown = null;
                this.seconds = 30;
                this.startCountdown();
              }
            }

            this.created_at = response.data.data.created_at;
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
        },
        (error) => {
          // No response at all on a network error or a proxy's HTML 502.
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.common.somethingWrong");
          this.setTimeOutNotification();
        }
      );
    },

    getMainItemPriceAndModifiersPrice(item, isNonMember) {
      // Use the already calculated price that includes modifiers
      var finalPrice = 0;

      if (this.tableCode) {
        finalPrice =
          item.is_member && item.member_price
            ? item.member_price
            : item.price_default;
      } else {
        finalPrice =
          isNonMember == false && item.member_price
            ? item.member_price
            : item.price_default;
      }

      return this.formatPrice(finalPrice * item.quantity);
    },

    getMainItem(item, isNonMember) {
      var priceMainAndModifer = 0;
      if (this.tableCode) {
        isNonMember = !item.is_member;
      }
      priceMainAndModifer =
        isNonMember == false && item.member_price_no_modifier_option
          ? item.price_default_no_modifier_option * item.quantity
          : 0;
      var totalPriceModifer = 0;

      if (item.choice) {
        if (item.choice_selected && item.choice_selected.items) {
          item.choice_selected.items.forEach((itemChoice) => {
            if (itemChoice.item_detail) {
              itemChoice.item_detail.modifier_group.forEach((group) => {
                group.items.forEach((itemModifier) => {
                  totalPriceModifer +=
                    itemModifier.price_default *
                    itemModifier.quantity *
                    itemChoice.qty;

                  if (
                    itemModifier.item_in_mog &&
                    itemModifier.item_in_mog.modifier_group
                  ) {
                    itemModifier.item_in_mog.modifier_group.forEach(
                      (subGroup) => {
                        subGroup.items.forEach((subItemModifier) => {
                          totalPriceModifer +=
                            subItemModifier.price_default *
                            subItemModifier.quantity *
                            itemModifier.quantity *
                            itemChoice.qty;
                        });
                      }
                    );
                  }
                });
              });
            }
          });
        }
      } else {
        item.modifier_group.forEach((group) => {
          group.items.forEach((itemModifier) => {
            totalPriceModifer +=
              itemModifier.price_default * itemModifier.quantity;

            if (
              itemModifier.item_in_mog &&
              itemModifier.item_in_mog.modifier_group
            ) {
              itemModifier.item_in_mog.modifier_group.forEach((subGroup) => {
                subGroup.items.forEach((subItemModifier) => {
                  totalPriceModifer +=
                    subItemModifier.price_default *
                    subItemModifier.quantity *
                    itemModifier.quantity;
                });
              });
            }
          });
        });
      }

      return priceMainAndModifer > 0
        ? this.formatPrice(
            priceMainAndModifer + totalPriceModifer * item.quantity
          )
        : "";
    },

    // Begin watching this order for POS confirmation.
    //
    // Re-entrant on purpose: retry() calls this after a failed order, and the
    // guard is what keeps a second call from extending a window that is already
    // running rather than restarting it.
    startPolling() {
      if (this.isDestroyed || this.intervalOrderStatus) return;

      this.pollDeadlineAt = Date.now() + POS_CONFIRM_WINDOW_MS;
      this.armTimers();
    },

    stopPolling() {
      clearInterval(this.intervalOrderStatus);
      this.intervalOrderStatus = null;
      clearTimeout(this.deadlineTimer);
      this.deadlineTimer = null;
      this.pollIntervalMs = null;
      // Last, because everything else reads it as "are we still watching?"
      this.pollDeadlineAt = 0;
    },

    // The fallback timer, plus the one that ends the wait.
    //
    // They are separate timers because they answer different questions. Folding
    // the deadline into the poll tick — which is how it worked when it was an
    // attempt count — ties how precisely we honour three minutes to how often we
    // happen to be polling, and with a socket up that is every thirty seconds.
    armTimers() {
      clearInterval(this.intervalOrderStatus);

      this.pollIntervalMs = this.realtimeConnected ? SLOW_POLL_MS : FAST_POLL_MS;
      this.intervalOrderStatus = setInterval(() => {
        // trailing: false — a tick names no particular change, so if a read is
        // already running it is exactly what this tick would have asked for.
        this.requestOrderRefresh(0, { trailing: false });
      }, this.pollIntervalMs);

      clearTimeout(this.deadlineTimer);
      this.deadlineTimer = setTimeout(
        () => this.givePosUp(),
        Math.max(0, this.pollDeadlineAt - Date.now())
      );
    },

    retimePolling() {
      if (this.isDestroyed || !this.intervalOrderStatus) return;

      const wanted = this.realtimeConnected ? SLOW_POLL_MS : FAST_POLL_MS;

      if (this.pollIntervalMs === wanted) return;

      this.armTimers();
    },

    // POS never confirmed inside the window. Same ending the 60th failed poll
    // attempt used to produce.
    givePosUp() {
      if (this.isDestroyed) return;

      this.stopPolling();

      if (this.orderDetail) {
        this.orderDetail.is_expired = 1;
      }

      this.seconds = 30;
      this.startCountdown();
    },

    /**
     * Every request to re-read the order goes through here — timer tick, socket
     * signal, reconnect resync — rather than calling getOrderDetail directly.
     *
     * That buys two things a bare call cannot: a burst of signals costs one API
     * read, and a signal that arrives mid-read is never silently dropped.
     */
    requestOrderRefresh(delayMs = COALESCE_MS, { trailing = true } = {}) {
      if (this.isDestroyed || !this.pollDeadlineAt) return;

      if (this.isRefreshing) {
        if (trailing) this.refreshPending = true;
        return;
      }

      // Something is already scheduled: join that window rather than opening one.
      if (this.refreshTimer) return;

      this.refreshTimer = setTimeout(() => {
        this.refreshTimer = null;
        this.runOrderCheck();
      }, delayMs);
    },

    /**
     * One read of the order plus the decision that belongs with it.
     *
     * No loader, unlike the poll this replaced. A full-screen spinner every three
     * seconds was already covering the order the customer is reading; once reads
     * are also triggered by arriving signals it would flash at moments nothing on
     * screen explains. The initial load in created() still shows one.
     */
    async runOrderCheck() {
      if (this.isDestroyed || !this.pollDeadlineAt) return;

      this.isRefreshing = true;
      // Cleared before the read, not after: anything arriving from here on is
      // about a write this read may have been too early to see.
      this.refreshPending = false;

      try {
        await this.getOrderDetail();
      } finally {
        this.isRefreshing = false;
      }

      this.finishOrderRead();
    },

    /**
     * What follows every completed read: settle, or honour a read requested
     * while it ran. Shared by runOrderCheck and the initial load in created().
     */
    finishOrderRead() {
      // getOrderDetail stops the watch itself on an error or an expired order.
      if (this.isDestroyed || !this.pollDeadlineAt) return;

      if (this.settleIfConfirmed()) return;

      // A signal landed mid-read. The read above cannot be trusted to include
      // it, so spend exactly one more.
      if (this.refreshPending) {
        this.refreshPending = false;
        this.requestOrderRefresh();
      }
    },

    /**
     * Show the success ending if the order last read is confirmed.
     *
     * Shared by the initial load and every later read. Ending the watch is what
     * makes it run once: confettiActive and the cleared deadline both turn a
     * second call into a no-op.
     */
    settleIfConfirmed() {
      if (this.isDestroyed || !this.pollDeadlineAt) return false;

      if (
        this.confettiActive ||
        !this.orderDetail ||
        this.orderDetail.status != TRANSACTION_STATUS_SUCCESS ||
        !(this.updateOrderStatusWithoutPOS ||
          this.orderDetail.is_pushed_to_megapos_database)
      ) {
        return false;
      }

      this.status = 1;
      this.confettiActive = true;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
      });

      this.startCountdown();
      this.stopPolling();
      return true;
    },

    // Realtime is best effort. If the token call fails, the server has it
    // switched off, or the socket never connects, the screen simply keeps
    // polling at the old three-second rate and nothing above this needs to know.
    async startRealtime() {
      if (this.isDestroyed || this.realtime || !this.transactionId) return;

      this.realtime = new OrderRealtime({
        fetchToken: async () => {
          const response = await KioskService.getWsTokenForOrder({
            transaction_id: this.transactionId,
            branch_code: this.branchCode,
          });

          const result = response.data.result;

          return result && result.isSuccess ? response.data.data : null;
        },
        onSignal: (signal) => {
          if (!signalNeedsRead(signal, this.orderDetail)) return;

          const delay =
            signal && signal.resync
              ? Math.random() * RESYNC_JITTER_MS
              : COALESCE_MS;

          this.requestOrderRefresh(delay);
        },
        onConnectionChange: (connected) => {
          this.realtimeConnected = connected;
          this.retimePolling();

          // The first connect slows polling to thirty seconds without a read of
          // its own, so a POS push that lands between the page's first read and
          // the socket joining would wait that long. Kiosks are few; one read
          // per order closes the gap.
          if (connected && !this.readOnFirstConnect) {
            this.readOnFirstConnect = true;
            this.requestOrderRefresh(0);
          }
        },
      });

      await this.realtime.start();
    },

    newOrder() {
      window.location.href = '/' + this.merchantCode + '/' + this.branchCode + '/kiosk';
    },

    retry() {
      this.showLoader();
      var dataForm = {
        branch_code: this.branchCode,
        transaction_id: this.transactionId,
      };

      KioskService.retryOrder(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.seconds = 20;
            clearInterval(this.intervalCountdown);
            this.intervalCountdown = null;
            this.message_error_from_megapos = "";
            this.orderDetail.is_pushed_to_megapos_database = null;
            this.orderDetail.is_expired = 0;
            this.startPolling();
          } else {
            this.snackbar = "danger";
            this.message = response.data.result.message;
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message =
            error.response?.data?.result?.message ||
            this.$t("kiosk.common.somethingWrong");
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    gotoPath(name) {
      this.$router.push({
        name: name,
        params: {
          merchant_code: this.merchantCode,
          branch_code: this.branchCode,
        },
      });
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },
  },
};
</script>

<style scoped>
.container-custom {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* KSK016 — one centred 760px column (same as the payment screens) between
   the header and the bottom bar: hero → order number → store → updates →
   items. */
.ksk-su__scroll {
  /* Success is always green, whatever the merchant's brand colour. */
  --ksk-su-success: #22a54a;
  --ksk-su-success-soft: #e2f5e7;
  position: fixed;
  top: var(--ksk-header-height);
  left: 0;
  right: 0;
  bottom: calc(112px + var(--ksk-powered-height));
  overflow-y: auto;
  padding: var(--ksk-space-4) var(--ksk-space-3);
  background: var(--ksk-background);
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}
.ksk-su__scroll::-webkit-scrollbar {
  display: none;
}
.ksk-su__scroll > * {
  width: 100%;
  max-width: 760px;
  margin-left: auto;
  margin-right: auto;
}
.ksk-su__card {
  margin-top: var(--ksk-space-2);
  padding: var(--ksk-space-3);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-card);
}
.ksk-su__card-title {
  margin: 0 0 var(--ksk-space-3);
  font-family: inherit;
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: var(--ksk-text);
}

/* Hero */
.ksk-su__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--ksk-space-2) 0 var(--ksk-space-3);
}
.ksk-su__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--ksk-su-success-soft);
  color: var(--ksk-su-success);
}
.ksk-su__check .material-icons-round {
  font-size: 72px;
}
.ksk-su__check--done {
  animation: ksk-su-pop 480ms ease-out;
}
@keyframes ksk-su-pop {
  0% { transform: scale(0.6); }
  60% { transform: scale(1.12); }
  100% { transform: scale(1); }
}
.ksk-su__title {
  margin: var(--ksk-space-3) 0 0;
  font-family: inherit;
  font-size: 40px;
  line-height: 48px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--ksk-text);
}
.ksk-su__subtitle {
  margin-top: 4px;
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  color: var(--ksk-text-secondary);
}

/* Order number */
.ksk-su__order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ksk-space-1);
  padding: var(--ksk-space-4) var(--ksk-space-3);
  text-align: center;
}
.ksk-su__eyebrow {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ksk-text-secondary);
}
.ksk-su__order-no {
  font-size: 72px;
  line-height: 80px;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--ksk-primary);
  overflow-wrap: anywhere;
}
.ksk-su__dining {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  margin-top: var(--ksk-space-1);
  padding: 0 var(--ksk-space-2);
  border: 1px solid var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
  font-size: 18px;
  font-weight: 600;
}
.ksk-su__dining .material-icons-round {
  font-size: 22px;
}

/* Store / transaction */
.ksk-su__info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ksk-su__info-branch {
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
}
.ksk-su__info-row {
  display: flex;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  font-size: 18px;
  line-height: 24px;
  color: var(--ksk-text-secondary);
}

/* Order updates: dots joined by a line */
.ksk-su__steps {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0 var(--ksk-space-2);
}
.ksk-su__step {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ksk-space-1);
  text-align: center;
}
.ksk-su__step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid var(--ksk-border);
  background: var(--ksk-surface);
  color: var(--ksk-on-primary);
}
.ksk-su__step-dot .material-icons-round {
  font-size: 26px;
}
.ksk-su__step--done .ksk-su__step-dot {
  border-color: var(--ksk-su-success);
  background: var(--ksk-su-success);
}
.ksk-su__step--current .ksk-su__step-dot {
  box-shadow: 0 0 0 6px var(--ksk-su-success-soft);
}
.ksk-su__step--fail .ksk-su__step-dot {
  border-color: var(--ksk-error);
  background: var(--ksk-error);
  box-shadow: none;
}
.ksk-su__step-label {
  font-size: 18px;
  line-height: 24px;
  font-weight: 600;
  color: var(--ksk-text-secondary);
}
.ksk-su__step--done .ksk-su__step-label,
.ksk-su__step--current .ksk-su__step-label {
  color: var(--ksk-text);
}
.ksk-su__step-line {
  flex: 1 1 auto;
  max-width: 200px;
  height: 3px;
  margin-top: 20px;
  border-radius: 2px;
  background: var(--ksk-border);
}
.ksk-su__step-line--done {
  background: var(--ksk-su-success);
}
/* Total row under the items */
.ksk-su__total {
  margin-top: var(--ksk-space-2);
  padding-top: var(--ksk-space-2);
  border-top: 2px solid var(--ksk-border);
}
.ksk-su__total-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
}
.ksk-su__total-amount {
  font-size: 28px;
  line-height: 36px;
  font-weight: 800;
  color: var(--ksk-primary);
  font-variant-numeric: tabular-nums;
}
.ksk-su__paid {
  margin-top: var(--ksk-space-3);
  padding-top: var(--ksk-space-2);
  border-top: 1px solid var(--ksk-border);
  font-size: 18px;
  line-height: 24px;
  color: var(--ksk-text-secondary);
}
.ksk-su__paid strong {
  color: var(--ksk-text);
  font-weight: 600;
}

/* POS failure: payment reference card */
.ksk-su__ref {
  width: 100%;
  max-width: 560px;
  margin-top: var(--ksk-space-4);
  text-align: left;
}
.ksk-su__ref-row {
  display: flex;
  justify-content: space-between;
  gap: var(--ksk-space-2);
  padding: 8px 0;
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
}
.ksk-su__ref-row + .ksk-su__ref-row {
  border-top: 1px solid var(--ksk-border);
}
.ksk-su__ref-row strong {
  min-width: 0;
  color: var(--ksk-text);
  font-weight: 600;
  text-align: right;
  overflow-wrap: anywhere;
}

/* Waiting for the POS: inline spinner in the disabled button */
.ksk-su__wait {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}
.ksk-su__wait-spinner {
  width: 24px;
  height: 24px;
  border: 3px solid var(--ksk-border);
  border-top-color: var(--ksk-text-secondary);
  border-radius: 50%;
  animation: ksk-su-spin 0.9s linear infinite;
}
@keyframes ksk-su-spin {
  to { transform: rotate(360deg); }
}

/* Order items */
.ksk-su__line {
  display: flex;
  align-items: flex-start;
  gap: var(--ksk-space-2);
  padding: 12px 0;
  border-bottom: 1px solid var(--ksk-border);
}
.ksk-su__line:first-child {
  padding-top: 0;
}
.ksk-su__line:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}
.ksk-su__img {
  flex: none;
  width: 64px;
  height: 64px;
  border-radius: 12px;
  object-fit: cover;
  background: var(--ksk-surface-muted);
}
.ksk-su__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ksk-su__name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ksk-space-2);
}
.ksk-su__qty {
  margin-right: 4px;
  font-variant-numeric: tabular-nums;
}
.ksk-su__name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
  word-break: break-word;
}
.ksk-su__pill {
  display: inline-block;
  margin-left: 8px;
  padding: 0 10px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
  font-size: 16px;
  line-height: 28px;
  font-weight: 700;
  vertical-align: 3px;
}
.ksk-su__bundle,
.ksk-su__choice-name {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
}
.ksk-su__choice-name {
  font-weight: 600;
  color: var(--ksk-text);
}
.ksk-su__choice :deep(.ksk-mods) {
  padding-left: var(--ksk-space-2);
}
.ksk-su__price {
  flex: none;
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ksk-su__price-old {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
  text-decoration: line-through;
}
.ksk-su__price-total {
  font-size: 22px;
  line-height: 30px;
  font-weight: 700;
}

/* POS failure (status layout from kiosk-theme2.css) */
.ksk-su__fail {
  padding-top: 64px;
}

/* Bottom bar */
.ksk-su__bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: var(--ksk-powered-height);
  z-index: 3;
  display: flex;
  align-items: center;
  height: 112px;
  padding: var(--ksk-space-2) var(--ksk-space-3);
  background: var(--ksk-surface);
  box-shadow: var(--ksk-shadow-bar);
  font-family: var(--ksk-font);
}
.ksk-su__bar-row {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-2);
  width: 100%;
}
.ksk-su__btn {
  flex: 1 1 0;
}
</style>
